import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'How to Invoice as a Photographer in the UK — HMRC, Licensing & Getting Paid',
      'description': 'A complete guide for UK freelance photographers: what goes on a photography invoice, licensing fees, deposit invoicing, VAT rules, and HMRC self-assessment.',
      'url': 'https://www.invilic.com/blog/how-to-invoice-as-photographer-uk',
      'datePublished': '2026-06-07',
      'dateModified': '2026-06-07',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Do photographers in the UK have to charge VAT?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Only if your taxable turnover exceeds £90,000 in any rolling 12-month period. Below this threshold you do not charge VAT and must not add it to your invoices. Photography services are standard-rated at 20% VAT once you are registered. Digital image delivery and print sales are also standard-rated. Register with HMRC within 30 days of exceeding the £90,000 threshold.' },
        },
        {
          '@type': 'Question',
          'name': 'Should photographers invoice a deposit before the shoot?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes. Most professional UK photographers require a non-refundable booking deposit — typically 25–50% of the total fee — to secure the date. Send a deposit invoice immediately after the client confirms the booking. State clearly on the invoice that it is a "Booking Deposit" and specify that the date is held only once the deposit is received. The final balance invoice is issued after the shoot, on delivery of the edited images.' },
        },
        {
          '@type': 'Question',
          'name': 'How do photographers invoice for licensing and usage rights?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Licensing is billed as a separate line item from shoot fees. Specify: the licence scope (e.g. "UK print and web rights only"), the licence duration (e.g. "12 months"), and the permitted use (e.g. "marketing collateral, website, social media"). Commercial photography licences should clearly state what usage is included and what requires a licence extension fee. Label the line item "Image Licence Fee" or "Usage Rights Fee" and invoice it separately from creative fees.' },
        },
        {
          '@type': 'Question',
          'name': 'What should a UK photographer invoice include?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A UK photography invoice must include: your full name or trading name and address; the client name and address; a unique sequential invoice number; the invoice date; a payment due date; itemised line items (shoot fee, editing/retouching fee, travel costs, licensing fee, print costs); the total amount; and your UK bank details (sort code and account number). If VAT registered, include your VAT number and show 20% VAT on each applicable line.' },
        },
        {
          '@type': 'Question',
          'name': 'Do UK photographers need to do Self Assessment?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, if you earn more than £1,000 from self-employed photography in a tax year. Register for Self Assessment at gov.uk and file an annual tax return by 31 January. You pay income tax on your profits and Class 4 National Insurance. Allowable expenses include camera equipment (capital allowances), memory cards, editing software (Adobe CC), insurance, studio hire, travel, and professional development.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const invoiceFields = [
  { field: 'Your full name or photography business name', note: 'Use your legal name as sole trader or your limited company name' },
  { field: 'Your address and contact details', note: 'Required on all UK business invoices' },
  { field: "Client's name and billing address", note: 'Full name and address of the client or company being billed' },
  { field: 'A unique sequential invoice number', note: 'e.g. PHOTO-2026-001 — HMRC requires sequential numbering' },
  { field: 'Invoice date and payment due date', note: 'Standard for event photographers: due on image delivery; commercial: Net 30' },
  { field: 'Shoot fee / day rate / half-day rate', note: 'The core creative fee for time on set or at the event' },
  { field: 'Post-production / editing fee', note: 'If billed separately — specify number of images and delivery format' },
  { field: 'Image licence fee (if applicable)', note: 'Specify scope, usage, duration, and territory — e.g. "UK digital, 12 months"' },
  { field: 'Travel and expenses', note: 'Mileage, accommodation, equipment hire — itemise each' },
  { field: 'VAT number and 20% VAT (if registered)', note: 'Only if VAT registered; show net, VAT, and gross separately' },
  { field: 'UK bank details (sort code + account number)', note: 'Bank name, account holder name, sort code, account number' },
];

export default function HowToInvoicePhotographerUkPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="How to Invoice as a Photographer in the UK — HMRC, Licensing & Getting Paid | Invilic"
        description="Complete guide for UK freelance photographers: invoice fields, deposit invoicing, image licensing fees, VAT at 20%, and HMRC self-assessment rules."
        canonical="https://www.invilic.com/blog/how-to-invoice-as-photographer-uk"
        ogType="article"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300">
              UK Invoicing
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">7 min read · June 7, 2026</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-6">
            How to Invoice as a Photographer in the UK — HMRC, Licensing & Getting Paid
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 leading-relaxed">
            Photography invoices have unique complexity: deposits before the shoot, creative fees on delivery, licensing on top, and travel expenses throughout. This guide covers every field a UK photographer needs on their invoice, plus HMRC and VAT rules specific to photography.
          </p>
        </div>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">What Must Be on a UK Photography Invoice?</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Photography invoices typically cover multiple billable elements. Every element should be on its own line so clients understand what they are paying for.
          </p>
          <div className="space-y-3">
            {invoiceFields.map(({ field, note }) => (
              <div key={field} className="flex items-start gap-3 p-4 rounded-xl border border-violet-100 dark:border-violet-800/30 bg-violet-50 dark:bg-violet-900/10">
                <CheckCircle className="w-4 h-4 text-violet-600 dark:text-violet-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">{field}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{note}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">The Photography Invoicing Timeline</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-5">
            UK photographers typically issue invoices at three separate points in the client workflow:
          </p>
          <div className="space-y-4">
            {[
              {
                step: '1',
                title: 'Booking deposit invoice',
                timing: 'Immediately after client confirms booking',
                detail: 'Invoice 25–50% of the total project fee. Mark clearly as "Booking Deposit — Non-Refundable." This holds the date in your calendar. State that the booking is only confirmed on receipt of the deposit.',
                color: 'blue',
              },
              {
                step: '2',
                title: 'Final balance invoice',
                timing: 'On delivery of edited images',
                detail: 'Invoice the remaining balance (total minus deposit already paid). Reference the deposit invoice number to show it has been applied. Show: shoot fee, editing fee, licensing fee, travel — with deposit deducted. For weddings, some photographers invoice the balance 14 days before the event.',
                color: 'violet',
              },
              {
                step: '3',
                title: 'Licensing extension invoice',
                timing: 'When usage rights are expanded',
                detail: 'If a client wants to use images beyond the original licence scope (e.g. new territory, extended period, or additional media), invoice a licence extension fee. Specify the new scope clearly and reference the original licence on the invoice.',
                color: 'indigo',
              },
            ].map(({ step, title, timing, detail, color }) => (
              <div key={title} className={`p-5 rounded-xl border border-${color}-100 dark:border-${color}-800/30 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className="flex items-start gap-4">
                  <div className={`w-8 h-8 rounded-full bg-${color}-600 text-white font-bold text-sm flex items-center justify-center flex-shrink-0`}>{step}</div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <p className="font-bold text-gray-900 dark:text-white">{title}</p>
                      <span className="text-xs text-gray-400 dark:text-gray-500">{timing}</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">How to Invoice Image Licensing Correctly</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-5">
            Licensing fees are one of the most misunderstood parts of photography invoicing. Your invoice should make the licence scope completely unambiguous.
          </p>
          <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 mb-5">
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Example licence line item:</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 font-mono bg-white dark:bg-gray-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
              Image Licence Fee — UK digital rights only (website, social media, email marketing) · 12 months from delivery date · Non-exclusive · No print rights included .......... £350.00
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { label: 'Specify the territory', detail: 'UK only, worldwide, or specific countries. Geographic scope directly affects the licence value.' },
              { label: 'Specify the media', detail: 'Digital (web, social, email), print (brochures, billboards), broadcast (TV, video ads), or all-media.' },
              { label: 'Specify the duration', detail: '12 months, 3 years, or perpetual. Perpetual licences command a premium and should be priced accordingly.' },
              { label: 'State exclusivity', detail: 'Exclusive licences (only the client can use the image) are worth significantly more than non-exclusive rights.' },
            ].map(({ label, detail }) => (
              <div key={label} className="p-4 rounded-xl border border-violet-100 dark:border-violet-800/30 bg-violet-50 dark:bg-violet-900/10">
                <p className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">VAT Rules for UK Photographers</h2>
          <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
            <p>Photography services are <strong className="text-gray-900 dark:text-white">standard-rated at 20% VAT</strong> — this includes shoot fees, editing, licensing, prints, and travel reimbursements billed to the client.</p>
            <p>You only charge VAT once your taxable turnover exceeds <strong className="text-gray-900 dark:text-white">£90,000</strong> in any rolling 12-month period. Below this threshold, no VAT is charged and you must not add it.</p>
            <p>For commercial photography clients, they can reclaim the VAT as input tax, so your VAT registration makes little difference to their actual cost. For private clients (weddings, portraits), VAT registration adds 20% to what they pay.</p>
            <p>Once VAT registered, show VAT separately on every invoice: net amount, VAT at 20%, and gross total. Include your VAT registration number prominently.</p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">HMRC and Tax for Self-Employed Photographers</h2>
          <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
            <p>Register for Self Assessment at gov.uk if you earn more than £1,000 from self-employed photography in a tax year. File your return and pay tax by 31 January each year.</p>
            <p><strong className="text-gray-900 dark:text-white">Allowable expenses</strong> that reduce your tax bill include: camera bodies and lenses (via HMRC Annual Investment Allowance), memory cards and hard drives, editing software (Adobe Lightroom, Photoshop — monthly subscription), studio hire, professional insurance, travel to shoots, website and marketing costs.</p>
            <p><strong className="text-gray-900 dark:text-white">Capital allowances</strong>: Major equipment purchases (cameras, lighting rigs) can be deducted in full in the year of purchase via the Annual Investment Allowance (£1 million limit per year — well above what most photographers spend).</p>
            <p>Keep copies of all invoices, receipts, and contracts for at least <strong className="text-gray-900 dark:text-white">6 years</strong>. Store them digitally alongside your image archives.</p>
          </div>
        </section>

        <BlogCTA
          heading="Create a Photography Invoice for Free"
          body="Professional UK PDF invoices with fields for shoot fees, licensing, and deposit tracking. No account needed."
          linkTo="/photographer-invoice-template"
          linkText="Get Free Photography Invoice Template"
        />

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        <RelatedArticles current="/blog/how-to-invoice-as-photographer-uk" />
      </main>
    </div>
  );
}
